/**
 * The visual vocabulary of the due diligence.
 *
 * The server keeps six certificate states; the screen shows ten, because
 * "pending" means different things depending on what already happened — did
 * the portal accept the request and will mail it? has the PDF arrived and the
 * AI has yet to read it? is personal data missing? `visualStatusOf` answers
 * that, and the rest of the UI reads its answer instead of the raw status.
 */
import {
  CERTIFICATE_STATE_LABEL,
  CERTIFICATE_STATES,
  DIVERGENCE_LABEL,
  READING_LABEL,
  REGISTRY_READING_LABEL,
  type CertificateState
} from '@server/domain/catalog/standing'
import { ORIGIN_LABEL } from '@server/domain/due-diligence'
import type { CertificateCategory, CertificateOrigin, DiligenceStatus, SubjectType } from '@server/domain/due-diligence'
import type { AbsenceStake } from '@server/domain/catalog/coverage'
import type { ReviewStage } from '@server/domain/review'
import type { CollectionStage } from '@server/domain/catalog/collection'
import type { CertificateView, DiligenceView } from '@server/routers/views'

import type { ThemeColor } from '@core/types'

/** What the API sends — the aggregates stay on the server. */
export type Certificate = CertificateView
export type Diligence = DiligenceView

/** The ten states a certificate can show, in three groups. */
export type VisualStatus =
  | 'clear'
  | 'warning'
  | 'adverse'
  | 'running'
  | 'awaitingEmail'
  | 'awaitingAi'
  | 'notStarted'
  | 'manual'
  | 'missingData'
  | 'mirrored'
  | 'failed'

/**
 * `resolved` — a final answer, nothing left to do.
 * `inFlight` — the system handles it alone, just wait.
 * `blocked`  — it stopped waiting on somebody.
 */
export type StatusGroup = 'resolved' | 'inFlight' | 'blocked'

export type StatusConfig = {
  label: string
  hint: string
  description: string
  color: ThemeColor
  icon: string
  group: StatusGroup
  groupLabel: string
  /** States that are still moving get the pulsing dot. */
  animated?: boolean
}

export const VISUAL_STATUS: Record<VisualStatus, StatusConfig> = {
  // The readings take their words from the domain: "Negativa" and "Positiva"
  // name kinds of certificate, not results (DIL-059).
  clear: {
    label: READING_LABEL.clear,
    hint: 'Nada consta',
    description: 'Certidão analisada — tudo certo, sem pendência.',
    color: 'success',
    icon: 'tabler-circle-check',
    group: 'resolved',
    groupLabel: 'Concluída'
  },
  warning: {
    label: READING_LABEL.warning,
    hint: 'Verificar',
    description: 'Encontrou processo ou pendência menor, sem bloqueio.',
    color: 'warning',
    icon: 'tabler-alert-triangle',
    group: 'resolved',
    groupLabel: 'Concluída'
  },
  adverse: {
    label: READING_LABEL.adverse,
    hint: 'Consta ocorrência',
    description: 'Pendência fiscal ou restrição confirmada — bloqueante.',
    color: 'error',
    icon: 'tabler-circle-x',
    group: 'resolved',
    groupLabel: 'Concluída'
  },
  running: {
    label: 'Processando',
    hint: 'Buscando no portal',
    description: 'O sistema está buscando esta certidão agora.',
    color: 'info',
    icon: 'tabler-loader-2',
    group: 'inFlight',
    groupLabel: 'Em progresso',
    animated: true
  },
  awaitingEmail: {
    label: 'Aguardando email',
    hint: 'O tribunal envia por email',
    description: 'Pedido aceito pelo órgão. O PDF chega por email e o sistema anexa sozinho.',
    color: 'info',
    icon: 'tabler-mail-forward',
    group: 'inFlight',
    groupLabel: 'Em progresso',
    animated: true
  },
  awaitingAi: {
    label: 'Aguardando IA',
    hint: 'Documento na fila de análise',
    description: 'O PDF já foi baixado e está na fila para a IA ler.',
    color: 'secondary',
    icon: 'tabler-sparkles',
    group: 'inFlight',
    groupLabel: 'Em progresso',
    animated: true
  },
  notStarted: {
    label: 'Não iniciada',
    hint: 'Aguardando você buscar',
    description: 'A busca automática está disponível, mas ainda não rodou.',
    color: 'secondary',
    icon: 'tabler-circle',
    group: 'blocked',
    groupLabel: 'Precisa ação'
  },
  manual: {
    label: 'Manual',
    hint: 'Upload necessário',
    description: 'Não automatizável. Anexe o PDF manualmente.',
    color: 'primary',
    icon: 'tabler-upload',
    group: 'blocked',
    groupLabel: 'Precisa ação'
  },
  mirrored: {
    label: 'Junto com outra',
    hint: 'A resposta vem de uma certidão irmã',
    description: 'O órgão responde as duas de uma vez — a busca roda uma vez só, nesta lista.',
    color: 'secondary',
    icon: 'tabler-arrow-merge',
    group: 'inFlight',
    groupLabel: 'Em progresso'
  },
  missingData: {
    label: 'Faltam dados',
    hint: 'Complete o cadastro',
    description: 'O portal exige dados extras da pessoa física antes de emitir.',
    color: 'warning',
    icon: 'tabler-user-exclamation',
    group: 'blocked',
    groupLabel: 'Precisa ação'
  },
  failed: {
    label: 'Falhou na busca',
    hint: 'Falhou — pode tentar de novo',
    description: 'Falha técnica no portal, no captcha ou na credencial.',
    color: 'error',
    icon: 'tabler-alert-circle',
    group: 'blocked',
    groupLabel: 'Precisa ação'
  }
}

/**
 * Which of the nine states this certificate is closest to.
 *
 * The order matters: "missing data" beats "failed" because the server marks
 * the certificate failed when the portal refuses for a missing field — and
 * what the person needs to see there is the button to complete the record,
 * not the one to retry.
 */
export const visualStatusOf = (certificate: Certificate): VisualStatus => {
  const { status } = certificate
  const fileName = certificate.fileName ?? ''
  const summary = certificate.summary ?? ''

  if (certificate.missingFields.length && (status === 'pending' || status === 'failed')) return 'missingData'

  // The server's own word for it: a missing credential names no field of the
  // person, and is just as deaf to a retry (DIL-058).
  if (status === 'failed' && certificate.failureKind === 'missingData') return 'missingData'

  if (status === 'clear') return 'clear'
  if (status === 'warning') return 'warning'
  if (status === 'adverse') return 'adverse'
  if (status === 'running') return 'running'
  if (status === 'failed') return 'failed'

  // A resposta vem da irmã, e não do portal: sem isto a certidão aparece como
  // "não iniciada" e alguém clica um botão de busca que nunca deveria existir.
  if (certificate.mirrorOf) return 'mirrored'

  // From here on it is 'pending' — several sub-states.
  if (!certificate.automated) return 'manual'

  if (fileName.startsWith('email_')) return 'awaitingAi'
  if (/análise ia não|ia não executada/i.test(summary)) return 'awaitingAi'

  // A request receipt: the certificate itself has not arrived yet.
  if (fileName.startsWith('proof_')) return 'awaitingEmail'
  if (/enviada por email|aguardando.*email|ainda não dispon[íi]vel/i.test(summary)) return 'awaitingEmail'

  if (fileName) return 'awaitingAi'

  return 'notStarted'
}

export const statusConfigOf = (certificate: Certificate): StatusConfig => {
  const status = visualStatusOf(certificate)
  const config = VISUAL_STATUS[status]

  // With no field of the person named, "dados da pessoa física" would send
  // somebody to the wrong form: what is missing is what the server said.
  if (status === 'missingData' && !certificate.missingFields.length) {
    return {
      ...config,
      hint: 'Falta dado que o portal exige',
      description: certificate.summary ?? 'O portal exige um dado ou uma configuração que ainda não foi informada.'
    }
  }

  // A clean registry datum informs; it does not certify anything regular.
  if (status === 'clear' && certificate.nature === 'registry') {
    return {
      ...config,
      label: REGISTRY_READING_LABEL.clear,
      hint: 'Dado cadastral',
      description: 'Dado cadastral: apenas informa. Não é certidão de situação, e não diz que algo está regular.'
    }
  }

  return config
}

// ─── What the collection is doing ───────────────────────────────────────────

export type { CollectionStage }

/** The four words the case screen is organized by, in the order a person reads them. */
export const COLLECTION_STAGES: readonly CollectionStage[] = ['needsYou', 'inProgress', 'noSource', 'obtained']

export const COLLECTION_LOOK: Record<CollectionStage, { label: string; color: ThemeColor; icon: string; description: string }> = {
  obtained: {
    label: 'Obtidas',
    color: 'success',
    icon: 'tabler-file-check',
    description: 'Vieram da fonte e já foram lidas.'
  },
  inProgress: {
    label: 'Em andamento',
    color: 'info',
    icon: 'tabler-refresh',
    description: 'O sistema está buscando, lendo ou vai tentar de novo sozinho — nada a fazer.'
  },
  needsYou: {
    label: 'Precisa de você',
    color: 'warning',
    icon: 'tabler-user-exclamation',
    description: 'Conta ou credencial de fornecedor, ou um dado que nenhuma base trouxe.'
  },
  noSource: {
    label: 'Sem fonte automática',
    color: 'secondary',
    icon: 'tabler-building-bank',
    description: 'Nenhuma base consultável entrega este documento — alguém precisa obtê-lo.'
  }
}

/** The stage the server decided, or `null` for a line sent without one. */
export const collectionStageOf = (certificate: Certificate): CollectionStage | null =>
  certificate.collection?.stage ?? null

/** Lines per stage, and the soonest moment the system goes back by itself. */
export const collectionSummaryOf = (subjects: Pick<Subject, 'certificates'>[]) => {
  const counts: Record<CollectionStage, number> = { obtained: 0, inProgress: 0, needsYou: 0, noSource: 0 }
  let nextAttemptAt: Date | null = null
  let total = 0

  for (const certificate of subjects.flatMap(subject => subject.certificates)) {
    const collection = certificate.collection

    if (!collection) continue

    total += 1
    counts[collection.stage] += 1

    const next = collection.nextAttemptAt ? new Date(collection.nextAttemptAt) : null

    if (next && (!nextAttemptAt || next < nextAttemptAt)) nextAttemptAt = next
  }

  return { counts, total, nextAttemptAt }
}

// ─── The four explicit states ───────────────────────────────────────────────

/**
 * How each of the domain's four states looks.
 *
 * Which state a certificate is in is decided on the server (`standing.state`,
 * from `catalog/standing.ts`) — the screen only paints it. The visual status
 * above stays as the detail inside the state: "Pendente · Aguardando email".
 */
export const CERTIFICATE_STATE_LOOK: Record<
  CertificateState,
  { label: string; color: ThemeColor; icon: string; description: string }
> = {
  obtained: {
    label: CERTIFICATE_STATE_LABEL.obtained,
    color: 'success',
    icon: 'tabler-file-check',
    description: 'O documento veio e foi lido.'
  },
  divergent: {
    label: CERTIFICATE_STATE_LABEL.divergent,
    color: 'warning',
    icon: 'tabler-arrows-diff',
    description: 'O documento veio, mas a leitura conflita com outra fonte sobre o mesmo pesquisado.'
  },
  pending: {
    label: CERTIFICATE_STATE_LABEL.pending,
    color: 'info',
    icon: 'tabler-hourglass',
    description: 'Ainda sem documento — a busca está a caminho ou na mesa de alguém.'
  },
  unavailable: {
    label: CERTIFICATE_STATE_LABEL.unavailable,
    color: 'error',
    icon: 'tabler-file-off',
    description: 'Sem documento, e esperar não traz um: o portal falhou, falta dado ou o órgão não emitiu.'
  }
}

export { CERTIFICATE_STATES, type CertificateState }

/** The state the server resolved, or `null` for a view sent without one. */
export const certificateStateOf = (certificate: Certificate): CertificateState | null =>
  certificate.standing?.state ?? null

/** Why the reading is not settled, in words, when it is divergent. */
export const divergenceTextOf = (certificate: Certificate): string | null =>
  certificate.standing?.divergence ? DIVERGENCE_LABEL[certificate.standing.divergence] : null

/** Certificates per explicit state, each split by the detail inside it. */
export const countByState = (certificates: Certificate[]) => {
  const counts = Object.fromEntries(
    CERTIFICATE_STATES.map(state => [state, { total: 0, byStatus: {} as Partial<Record<VisualStatus, number>> }])
  ) as Record<CertificateState, { total: number; byStatus: Partial<Record<VisualStatus, number>> }>

  for (const certificate of certificates) {
    const state = certificateStateOf(certificate)

    if (!state) continue

    const status = visualStatusOf(certificate)

    counts[state].total += 1
    counts[state].byStatus[status] = (counts[state].byStatus[status] ?? 0) + 1
  }

  return counts
}

/**
 * Whether the search button has anything to do here.
 *
 * The same question `isQueueable()` answers on the server, asked of what the
 * screen already holds — so the button is not offered for a certificate the
 * portal would refuse. The server decides either way; this only spares the
 * person a click that would come back as an error.
 *
 * "Missing data" is deliberately out: those certificates get the button that
 * completes the record instead, which is what unblocks them.
 */
export const isSearchable = (certificate: Certificate): boolean => {
  const status = visualStatusOf(certificate)

  return (
    certificate.automated &&
    !certificate.mirrorOf &&
    !certificate.fileName &&
    (status === 'notStarted' || status === 'failed')
  )
}

// ─── Counting ───────────────────────────────────────────────────────────────

export type CertificateStats = ReturnType<typeof countCertificates>

const EMPTY_COUNT: Record<VisualStatus, number> = {
  clear: 0,
  warning: 0,
  adverse: 0,
  running: 0,
  awaitingEmail: 0,
  awaitingAi: 0,
  notStarted: 0,
  manual: 0,
  missingData: 0,
  mirrored: 0,
  failed: 0
}

/** Counts certificates by visual state and by the three macro groups. */
export const countCertificates = (certificates: Certificate[]) => {
  const byStatus = { ...EMPTY_COUNT }

  for (const certificate of certificates) byStatus[visualStatusOf(certificate)] += 1

  const resolved = byStatus.clear + byStatus.warning + byStatus.adverse
  const inFlight = byStatus.running + byStatus.awaitingEmail + byStatus.awaitingAi + byStatus.mirrored
  const blocked = byStatus.notStarted + byStatus.manual + byStatus.missingData + byStatus.failed

  return {
    total: certificates.length,
    ...byStatus,
    resolved,
    inFlight,
    blocked,
    issues: byStatus.warning + byStatus.adverse + byStatus.failed + byStatus.missingData
  }
}

export const statsOf = (diligence: Diligence) =>
  countCertificates(diligence.subjects.flatMap(subject => subject.certificates))

// ─── Where the case stands ──────────────────────────────────────────────────

/**
 * The stretch of work the case is in.
 *
 * It decides what the screen opens on and which action it offers first: a
 * case still collecting needs its certificates, not its delivery form.
 */
export type CasePhase = 'collecting' | 'analyzing' | 'releasing' | 'closed'

export const CASE_PHASE: Record<CasePhase, { label: string; color: ThemeColor }> = {
  collecting: { label: 'Coleta', color: 'info' },
  analyzing: { label: 'Análise', color: 'primary' },
  releasing: { label: 'Pronto para entregar', color: 'success' },
  closed: { label: 'Encerrado', color: 'secondary' }
}

export const casePhaseOf = (input: {
  status: DiligenceStatus
  stage: ReviewStage | null
  stats: Pick<CertificateStats, 'blocked' | 'inFlight'>
}): CasePhase => {
  if (input.status !== 'inProgress') return 'closed'
  if (input.stage === 'released') return 'releasing'
  if (input.stats.blocked + input.stats.inFlight > 0) return 'collecting'

  return 'analyzing'
}

/** Work that waits on a person — each one a line of "next steps", with its own button. */
export type NextStepKind = 'legalPurpose' | 'missingData' | 'failed' | 'notStarted' | 'awaitingAi' | 'manual'

/**
 * What a document still out costs the case if it never comes, as the server
 * read it off the matrix. Never decided here: it is the same word the traffic
 * light is built on.
 */
export type { AbsenceStake }

export type StakeOf = { subjectId: string; itemId: string; stake: AbsenceStake }

/** How many of a step's documents the opinion cannot go without, or only with a caveat. */
export type StepStakes = { blocks: number; caveat: number }

/**
 * One person and what the portals asked of them.
 *
 * A person fills a field, not a certificate: "the mother's name of João frees
 * six" is the job, "six certificates missing data" is a list to decode.
 * `fields` is empty when what is missing is not the person's — a credential or
 * a configuration the portal needs.
 */
export type MissingDataGroup = {
  subjectId: string
  subjectName: string
  fields: Certificate['missingFields']
  certificates: number
}

export type NextStep = {
  kind: NextStepKind
  count: number
  stakes: StepStakes
  /** Missing data only: who to complete, and with what. */
  groups?: MissingDataGroup[]
}

type StepSubject = Pick<Subject, 'id' | 'name' | 'certificates'>

const NO_STAKES: StepStakes = { blocks: 0, caveat: 0 }

const COUNTED: Exclude<NextStepKind, 'legalPurpose'>[] = ['missingData', 'failed', 'notStarted', 'awaitingAi', 'manual']

/**
 * Whether the system is already on this line by itself — queued, fetching,
 * reading, waiting for an e-mail or for its next try. Not a step for a person:
 * "tentar de novo" on a line the sweep retries at 14:30 was work for nobody.
 */
const handledBySystem = (certificate: Certificate): boolean =>
  certificate.collection?.stage === 'inProgress' || certificate.collection?.stage === 'obtained'

const groupsOf = (subjects: StepSubject[]): MissingDataGroup[] =>
  subjects.flatMap(subject => {
    const blocked = subject.certificates.filter(
      certificate =>
        !certificate.groundBlocked && !handledBySystem(certificate) && visualStatusOf(certificate) === 'missingData'
    )
    const ofPerson = blocked.filter(certificate => certificate.missingFields.length)
    const notOfPerson = blocked.length - ofPerson.length
    const fields = [...new Set(ofPerson.flatMap(certificate => certificate.missingFields))]

    return [
      ...(ofPerson.length
        ? [{ subjectId: subject.id, subjectName: subject.name, fields, certificates: ofPerson.length }]
        : []),
      ...(notOfPerson ? [{ subjectId: subject.id, subjectName: subject.name, fields: [], certificates: notOfPerson }] : [])
    ]
  })

/**
 * What depends on the person, most unblocking first.
 *
 * The purpose comes before everything: without it the bureau queries are
 * refused, so the bureau lines wait on it and on nothing else. Missing data
 * comes before failures because a retry cannot fix what the portal refused for
 * a missing field.
 *
 * Each step carries how many of its documents block the opinion or raise a
 * caveat — the order is the system's, the weight is the lawyer's.
 */
export const nextStepsOf = (input: {
  subjects: StepSubject[]
  purposeMissing: boolean
  stakes?: readonly StakeOf[]
}): NextStep[] => {
  const stakeByLine = new Map((input.stakes ?? []).map(entry => [`${entry.subjectId}|${entry.itemId}`, entry.stake]))
  const tally = new Map<NextStepKind, NextStep>()

  const count = (kind: NextStepKind, subjectId: string, certificate: Certificate) => {
    const step = tally.get(kind) ?? { kind, count: 0, stakes: { ...NO_STAKES } }
    const stake = certificate.itemId ? stakeByLine.get(`${subjectId}|${certificate.itemId}`) : undefined

    step.count += 1
    if (stake === 'blocks') step.stakes.blocks += 1
    if (stake === 'caveat') step.stakes.caveat += 1
    tally.set(kind, step)
  }

  for (const subject of input.subjects) {
    for (const certificate of subject.certificates) {
      if (certificate.groundBlocked) {
        count('legalPurpose', subject.id, certificate)
        continue
      }

      if (handledBySystem(certificate)) continue

      const status = visualStatusOf(certificate)

      if ((COUNTED as VisualStatus[]).includes(status)) count(status as NextStepKind, subject.id, certificate)
    }
  }

  const purpose: NextStep[] = input.purposeMissing
    ? [tally.get('legalPurpose') ?? { kind: 'legalPurpose', count: 0, stakes: { ...NO_STAKES } }]
    : []

  return [
    ...purpose,
    ...COUNTED.flatMap(kind => {
      const step = tally.get(kind)

      if (!step) return []

      return [kind === 'missingData' ? { ...step, groups: groupsOf(input.subjects) } : step]
    })
  ]
}

/** What the API sends for one subject. */
export type Subject = Diligence['subjects'][number]

export const percentOf = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0)

// ─── Labels ─────────────────────────────────────────────────────────────────

export const SUBJECT_TYPE: Record<SubjectType, { label: string; description: string; icon: string }> = {
  cpf: { label: 'CPF', description: 'Pessoa física', icon: 'tabler-user' },
  cnpj: { label: 'CNPJ', description: 'Pessoa jurídica', icon: 'tabler-building-skyscraper' },
  property: { label: 'Matrícula', description: 'Imóvel', icon: 'tabler-home' }
}

/**
 * How the document arrived, for the screen.
 *
 * The labels are the domain's — the dossier prints the same words — and only
 * the icon is decided here. The delivery looks identical either way; this is
 * the one place an auditor sees which way it was.
 */
export const ORIGIN_DISPLAY: Record<CertificateOrigin, { label: string; icon: string }> = {
  scraper: { label: ORIGIN_LABEL.scraper, icon: 'tabler-robot' },
  provider: { label: ORIGIN_LABEL.provider, icon: 'tabler-plug-connected' },
  manual: { label: ORIGIN_LABEL.manual, icon: 'tabler-user-check' },
  email: { label: ORIGIN_LABEL.email, icon: 'tabler-mail' },
  mirror: { label: ORIGIN_LABEL.mirror, icon: 'tabler-copy' },
  counterparty: { label: ORIGIN_LABEL.counterparty, icon: 'tabler-user-share' }
}

export const CATEGORY_LABEL: Record<CertificateCategory, string> = {
  tax: 'Tributária',
  judicial: 'Judicial',
  labor: 'Trabalhista',
  criminal: 'Criminal',
  compliance: 'Compliance',
  registration: 'Cadastral',
  propertyRegistry: 'Registro de imóveis',
  financial: 'Fiscal',
  environmental: 'Ambiental',
  technical: 'Técnica'
}

export const DILIGENCE_STATUS: Record<DiligenceStatus, { label: string; color: ThemeColor }> = {
  inProgress: { label: 'Em andamento', color: 'info' },
  approved: { label: 'Aprovado', color: 'success' },
  approvedWithNotes: { label: 'Aprovado c/ ressalvas', color: 'warning' },
  rejected: { label: 'Reprovado', color: 'error' },
  /** Anonymized: the shell is kept as evidence, the data is gone. */
  purged: { label: 'Expurgada', color: 'secondary' }
}

/** The four a person may choose. `purged` is a consequence, never a choice. */
export const SELECTABLE_DILIGENCE_STATUS = ['inProgress', 'approved', 'approvedWithNotes', 'rejected'] as const

export const PERSONAL_FIELD_LABEL: Record<string, string> = {
  nationalId: 'RG',
  birthDate: 'Data de nascimento',
  motherName: 'Nome da mãe',
  gender: 'Gênero'
}

// ─── Formatting ─────────────────────────────────────────────────────────────

export const formatDate = (value: Date | string | null | undefined) => {
  if (!value) return '—'

  const date = value instanceof Date ? value : new Date(value)

  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR')
}

export const formatDateTime = (value: Date | string | null | undefined) => {
  if (!value) return '—'

  const date = value instanceof Date ? value : new Date(value)

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
}

/** An amount in centavos, as a person reads it — every Money crosses the wire as centavos. */
export const formatCents = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '—' : formatBrl(cents / 100)

export const formatBrl = (value: number | null | undefined, fractionDigits = 2) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(Number(value))

export const formatUsd = (value: number | null | undefined) =>
  value === null || value === undefined
    ? null
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value))

/**
 * A duration in the largest unit that still says something.
 *
 * "2.880 min" is a number nobody converts in their head; "2 d" is the answer.
 * Minutes below an hour keep their precision — that is the range where the
 * difference between eight and forty minutes matters.
 */
export const formatMinutes = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'

  const minutes = Math.round(Number(value))

  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)

  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`

  const days = Math.floor(hours / 24)

  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`
}

/** A rate that was never measured is a dash, never `0%`. */
export const formatPercent = (value: number | null | undefined, fractionDigits = 0) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '—'
    : `${(Number(value) * 100).toFixed(fractionDigits)}%`

/** How a deadline reads: what is left, or how long it has been past. */
export const formatDaysLeft = (days: number) => {
  if (days === 0) return 'vence hoje'
  if (days < 0) return `vencida há ${pluralize(Math.abs(days), 'dia', 'dias')}`

  return `${pluralize(days, 'dia', 'dias')}`
}

/** Simple plural, so `x === 1 ? 'análise' : 'análises'` is not written twice. */
export const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`

// ─── Input masks ────────────────────────────────────────────────────────────

export const maskCpf = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

export const maskCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 14)

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

export const maskDate = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8)

  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

/** The Brazilian ID has no national format; the SP/RJ pattern covers most. */
export const maskNationalId = (value: string) => {
  const raw = value
    .replace(/[^\dXx]/g, '')
    .toUpperCase()
    .slice(0, 9)

  if (raw.length <= 2) return raw
  if (raw.length <= 5) return `${raw.slice(0, 2)}.${raw.slice(2)}`
  if (raw.length <= 8) return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5)}`

  return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}-${raw.slice(8)}`
}

export const maskDocument = (type: SubjectType, value: string) => {
  if (type === 'cpf') return maskCpf(value)
  if (type === 'cnpj') return maskCnpj(value)

  return value
}

export const digitsOnly = (value: string) => value.replace(/\D/g, '')

/** Check digits — the CPF lookup is paid, no point spending it on a typo. */
export const isValidCpf = (value: string) => {
  const cpf = digitsOnly(value)

  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false

  for (const stop of [9, 10]) {
    let sum = 0

    for (let position = 0; position < stop; position++) sum += Number(cpf[position]) * (stop + 1 - position)

    let digit = (sum * 10) % 11

    if (digit === 10) digit = 0
    if (digit !== Number(cpf[stop])) return false
  }

  return true
}

/** What the server says about an expansion before it runs — the ceiling of people and of money. */
export type ExpansionLimitsView = {
  ceiling: number
  alreadyExpanded: number
  remaining: number
  budgetLimitCents: number | null
  spentCents: number
  exceedsBudget: boolean
}

/**
 * The sentence an expansion is confirmed on: how many, what it costs, and
 * where the case stands against both ceilings. Said before the click, because
 * the number is the decision.
 */
export const describeExpansion = (count: number, totalCents: number, limits: ExpansionLimitsView): string =>
  [
    `${pluralize(count, 'pessoa entra', 'pessoas entram')} no caso, cada uma com o checklist do seu papel.`,
    `Custo estimado das buscas: ${formatCents(totalCents)} (somado pessoa a pessoa).`,
    `Expansões neste caso: ${limits.alreadyExpanded} de ${limits.ceiling} — ${
      count > limits.remaining ? `só cabem mais ${limits.remaining}, e o pedido será recusado` : `restam ${limits.remaining}`
    }.`,
    limits.budgetLimitCents !== null
      ? `Teto do caso: ${formatCents(limits.budgetLimitCents)}, gasto até agora ${formatCents(limits.spentCents)}${
          limits.exceedsBudget ? ' — passa do teto e precisa de autorização de um administrador' : ''
        }.`
      : ''
  ]
    .filter(Boolean)
    .join(' ')

/**
 * What the opinion button does: wait, open what is kept, or write the first
 * version.
 *
 * Writing is a paid model call, so it happens only when the list of versions
 * loaded and came back empty. While the list loads — or when it failed — the
 * case may well have opinions nobody has seen yet, and calling the model then
 * paid for one more (DIL-129, re-test).
 *
 * And while a version is being written, the button only shows how it is
 * going: the list still reads empty until it lands, and asking again would
 * pay the model a second time for the same dossier.
 */
export const opinionOpeningOf = (query: {
  status: 'pending' | 'error' | 'success'
  versions: readonly unknown[] | undefined
  /** Whether a version is being written right now. */
  writing?: boolean
}): 'wait' | 'open' | 'write' | 'watch' => {
  if (query.writing) return 'watch'
  if (query.status !== 'success' || !query.versions) return 'wait'

  return query.versions.length ? 'open' : 'write'
}
